/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  IconDownload,
  IconPrinter,
  IconRotateClockwise,
  IconSearch,
  IconChevronLeft,
  IconChevronRight,
  IconX,
} from "@tabler/icons-react";
import toast from "react-hot-toast";
import AllOrderTable from "./AllOrderTable";
import formatDate from "../../../utilities/helper";
import * as XLSX from "xlsx";
import { useFetchOrderOperations } from "../../../apis/operationApis";
import { useIntegrationChannelsAll } from "../../../apis/integrationApis";
import { useGeneratePicklistMutation } from "../../../apis/picklistApis";
import { useMerchantAllListData } from "../../../apis/merchantApis";
import { useFetchDarkStoreAllData } from "../../../apis/darkStoreApi";
import useDebounce from "../../../utilities/useDebounce";
import BulkStatusUpdateModal from "./BulkStatusUpdateModal";
import { IconPackage } from "@tabler/icons-react";
import NewSelectInput from "../../../components/NewSelectInput";
import EntriesSelector from "../../../components/common/EntriesSelector";
import ColumnsFilter from "../../../components/common/ColumnsFilter";
import ViewOrderDetailsSidebar from "../../OrderManagement/_components/ViewOrderDetailsSidebar";
import { usePermission } from "../../../hooks/usePermissionTabs";
import { useLanguage } from "../../../context/LanguageContext";

type Props = {
  pending?: boolean;
  setHeaderActions?: (node: React.ReactNode) => void;
  setHeaderButtons?: (node: React.ReactNode) => void;
};

const PAGE_LIMIT = 10;

export default function AllOrders({
  pending,
  setHeaderActions,
  setHeaderButtons,
}: Props) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { allowed: canUpdateOrder } = usePermission("orders", "update");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [searchFor, setSearchFor] = useState<string>("");
  const [selectedMerchant, setSelectedMerchant] = useState<string>("All");
  const [selectedPlatform, setSelectedPlatform] = useState<string>("All");
  const [selectedOrders, setSelectedOrders] = useState<any[]>([]);
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  // Columns visibility
  const [columns, setColumns] = useState([
    { id: 1, text: "S/N", value: "sn", isShow: false },
    { id: 2, text: "TAG", value: "tag", isShow: true },
    { id: 3, text: "DATE", value: "createdDate", isShow: true },
    { id: 4, text: "PLATFORM", value: "platform", isShow: true },
    { id: 5, text: "AWB & REF", value: "awbRef", isShow: true },
    { id: 6, text: "WAREHOUSE", value: "warehouse", isShow: true },
    { id: 7, text: "ROUTE", value: "route", isShow: true },
    {
      id: 8,
      text: "3PL SHIPPING DETAILS",
      value: "shippingDetails",
      isShow: true,
    },
    { id: 9, text: "ORIGIN", value: "origin", isShow: true },
    { id: 10, text: "DESTINATION", value: "destination", isShow: true },
    { id: 11, text: "RECEIVER", value: "receiverDetails", isShow: true },
    { id: 12, text: "PAYMENT", value: "paymentInfo", isShow: true },
    { id: 13, text: "MERCHANT", value: "merchant", isShow: true },
    { id: 14, text: "STATUS", value: "status", isShow: true },
    { id: 15, text: "ACTIONS", value: "actions", isShow: true },
  ]);

  const [selectedViewOrderAwb, setSelectedViewOrderAwb] = useState<
    string | null
  >(null);
  const showRightPanel = showBulkStatusModal;

  // Track initial/default columns to restore them when sidebar is closed
  const [defaultVisibleColumns, setDefaultVisibleColumns] = useState<string[]>(
    [],
  );
  useEffect(() => {
    if (columns.length > 0 && defaultVisibleColumns.length === 0) {
      const visible = columns
        .filter((col) => col.isShow)
        .map((col) => col.value);
      setDefaultVisibleColumns(visible);
    }
  }, [columns]);

  // Handle auto-shrinking table columns when right sidebar is open
  useEffect(() => {
    if (showRightPanel) {
      const essentialColumns = [
        "tag",
        "awbRef",
        "route",
        "platform",
        "merchant",
        "status",
        "actions",
      ];
      setColumns((prev) =>
        prev.map((col) => ({
          ...col,
          isShow: essentialColumns.includes(col.value),
        })),
      );
    } else if (defaultVisibleColumns.length > 0) {
      setColumns((prev) =>
        prev.map((col) => ({
          ...col,
          isShow: defaultVisibleColumns.includes(col.value),
        })),
      );
    }
  }, [showRightPanel, defaultVisibleColumns]);

  const handleSetHeadings = (value: string | undefined, checked: boolean) => {
    setColumns((prev) =>
      prev.map((col) =>
        col.value === value ? { ...col, isShow: checked } : col,
      ),
    );
  };

  const debouncedSearchFor = useDebounce(searchFor, 550);

  const activeFilters = useMemo(() => {
    const filters: any[] = [];
    if (selectedMerchant !== "All") filters.push({ seller: selectedMerchant });
    if (selectedPlatform !== "All")
      filters.push({ platformOrShippingCompany: selectedPlatform });
    return filters;
  }, [selectedMerchant, selectedPlatform]);

  // Fetch only ORDER_CREATED orders from backend, server-side paginated with filters
  const {
    data: response,
    isLoading,
    refetch,
  } = useFetchOrderOperations(
    "ORDER_CREATED",
    currentPage,
    limit,
    debouncedSearchFor,
    activeFilters,
  );
  const { mutate: generatePicklist, isPending: isGeneratingPicklist } =
    useGeneratePicklistMutation();
  const orders = response?.data ?? [];
  const pagination = response?.pagination;
  const totalPages = pagination?.totalPages ?? 1;
  const totalOrders = pagination?.total ?? 0;

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
    setSelectedOrders([]);
  }, [debouncedSearchFor, selectedMerchant, selectedPlatform]);

  const { data: shippingPlatforms } = useIntegrationChannelsAll("shipping");
  const { data: clients } = useMerchantAllListData();
  const { data: darkStores } = useFetchDarkStoreAllData();

  // Build dynamic filter options from all clients and dark stores
  const uniqueMerchants = useMemo(() => {
    const names = new Set<string>();
    if (clients && Array.isArray(clients)) {
      clients.forEach((c: any) => {
        if (c.name) names.add(c.name);
      });
    }
    if (darkStores && Array.isArray(darkStores)) {
      darkStores.forEach((ds: any) => {
        if (ds.name) names.add(ds.name);
      });
    }
    return Array.from(names);
  }, [clients, darkStores]);

  // Merge specific e-commerce platforms and dynamic 3PL companies
  const uniquePlatforms = useMemo(() => {
    const set = new Set<string>();
    set.add("Salla");
    set.add("Zid");
    if (shippingPlatforms) {
      shippingPlatforms.forEach((sp: any) => {
        if (sp.companyName) set.add(sp.companyName);
      });
    }
    return Array.from(set);
  }, [shippingPlatforms]);

  // Client-side filtering is no longer needed since the server filters,
  // but to maintain exact reactivity without visual layout shifts,
  // we align client-side matching.
  const filteredOrders = useMemo(() => {
    return orders.filter((order: any) => {
      const matchesSearch =
        searchFor === "" ||
        order.awbNumber?.toLowerCase().includes(searchFor.toLowerCase()) ||
        order.referenceNumber
          ?.toLowerCase()
          .includes(searchFor.toLowerCase()) ||
        order.receiverName?.toLowerCase().includes(searchFor.toLowerCase()) ||
        order.receiverMobile?.toLowerCase().includes(searchFor.toLowerCase()) ||
        order.receiverEmail?.toLowerCase().includes(searchFor.toLowerCase()) ||
        order.receiverCityName
          ?.toLowerCase()
          .includes(searchFor.toLowerCase()) ||
        order.receiverCountryName
          ?.toLowerCase()
          .includes(searchFor.toLowerCase()) ||
        order.receiverDistrictName
          ?.toLowerCase()
          .includes(searchFor.toLowerCase()) ||
        order.receiverAddress
          ?.toLowerCase()
          .includes(searchFor.toLowerCase()) ||
        order.receiverNationalAddress
          ?.toLowerCase()
          .includes(searchFor.toLowerCase()) ||
        order.senderName?.toLowerCase().includes(searchFor.toLowerCase()) ||
        order.senderCityName?.toLowerCase().includes(searchFor.toLowerCase()) ||
        order.warehouseName?.toLowerCase().includes(searchFor.toLowerCase()) ||
        order.clientName?.toLowerCase().includes(searchFor.toLowerCase()) ||
        order.platform?.toLowerCase().includes(searchFor.toLowerCase()) ||
        order.shippingCompany
          ?.toLowerCase()
          .includes(searchFor.toLowerCase()) ||
        order.shippingAwbNumber
          ?.toLowerCase()
          .includes(searchFor.toLowerCase()) ||
        order.paymentType?.toLowerCase().includes(searchFor.toLowerCase()) ||
        String(order.codAmount)
          .toLowerCase()
          .includes(searchFor.toLowerCase()) ||
        order.status?.toLowerCase().includes(searchFor.toLowerCase()) ||
        (order.createdAt &&
          formatDate(order.createdAt)
            .toLowerCase()
            .includes(searchFor.toLowerCase())) ||
        order.orderType?.toLowerCase().includes(searchFor.toLowerCase()) ||
        order.olineItems?.some(
          (item: any) =>
            item.sku?.toLowerCase().includes(searchFor.toLowerCase()) ||
            item.itemName?.toLowerCase().includes(searchFor.toLowerCase()),
        );

      const matchesMerchant =
        selectedMerchant === "All" ||
        order.clientName?.toLowerCase() === selectedMerchant.toLowerCase();
      const matchesPlatform =
        selectedPlatform === "All" ||
        order.platform?.toLowerCase() === selectedPlatform.toLowerCase() ||
        order.shippingCompany?.toLowerCase() === selectedPlatform.toLowerCase();

      return matchesSearch && matchesMerchant && matchesPlatform;
    });
  }, [searchFor, selectedMerchant, selectedPlatform, orders]);

  // Select / Deselect order
  const handleSelectOrder = (order: any) => {
    setSelectedOrders((prev) => {
      if (prev.some((o) => o.id === order.id))
        return prev.filter((o) => o.id !== order.id);
      return [...prev, order];
    });
  };

  // Reset search and filters
  const handleReset = () => {
    setSearchFor("");
    setSelectedMerchant("All");
    setSelectedPlatform("All");
    setSelectedOrders([]);
    setCurrentPage(1);
    toast.success("Filters reset successfully");
  };

  const hasActiveFilters =
    searchFor !== "" ||
    selectedMerchant !== "All" ||
    selectedPlatform !== "All";

  const handleGeneratePicklist = () => {
    if (selectedOrders.length === 0) {
      toast.error("Please select at least one order to generate a picklist");
      return;
    }

    const clientIds = Array.from(
      new Set(selectedOrders.map((order) => order.clientId).filter(Boolean)),
    );
    if (clientIds.length === 0) {
      toast.error("Could not determine merchant for the selected orders");
      return;
    }
    if (clientIds.length > 1) {
      toast.error("Selected orders must belong to the same merchant");
      return;
    }
    const clientId = clientIds[0] as string;

    const warehouseIds = Array.from(
      new Set(selectedOrders.map((order) => order.warehouseId).filter(Boolean)),
    );
    const warehouseId =
      warehouseIds.length === 1 ? (warehouseIds[0] as string) : undefined;

    const orderIds = selectedOrders.map((order) => order.id);

    generatePicklist(
      {
        type: "SINGLE_ORDER",
        orderIds,
        clientId,
        warehouseId,
      },
      {
        onSuccess: () => {
          toast.success("Picklist generated successfully!");
          setSelectedOrders([]);
          queryClient.invalidateQueries({ queryKey: ["picklists"] });
          refetch();
        },
        onError: (err: any) => {
          const errMsg = err?.message || err || "Failed to generate picklist";
          toast.error(errMsg);
          console.error("Generate picklist error:", err);
        },
      },
    );
  };

  const handleExportXLSX = () => {
    const dataToExport =
      selectedOrders.length > 0 ? selectedOrders : filteredOrders;
    if (dataToExport.length === 0) {
      toast.error("No data available to export");
      return;
    }
    const data = dataToExport.map((order: any, idx: number) => ({
      "S/N": idx + 1,
      Tag: order.orderType,
      "Order Date": new Date(order.createdAt).toLocaleDateString(),
      Platform: order.platform,
      "AWB Number": order.awbNumber,
      Reference: order.referenceNumber,
      Warehouse: order.warehouseName,
      Route: order.routeCode,
      "Shipping Company": order.shippingCompany,
      "Shipping AWB": order.shippingAwbNumber,
      Origin: order.senderCityName,
      "Destination Country": order.receiverCountryName,
      "Destination City": order.receiverCityName,
      "Destination District": order.receiverDistrictName,
      "Receiver Name": order.receiverName,
      "Receiver Mobile": order.receiverMobile,
      "Payment Mode": order.paymentType,
      "Payment Amount": order.codAmount,
      Merchant: order.clientName,
      Status: order.status,
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Orders");
    XLSX.writeFile(wb, "order_allocations_export.xlsx");
    toast.success("Exported successfully!");
  };

  useEffect(() => {
    if (setHeaderActions) setHeaderActions(null);
  }, [setHeaderActions]);

  useEffect(() => {
    if (!setHeaderButtons) return;

    setHeaderButtons(
      <div className="flex items-center gap-2 bg-white/70 backdrop-blur-sm px-4 py-1.5 rounded-xl border border-slate-200 shadow-sm text-[11px] select-none font-bold min-h-8">
        <div className="flex items-center gap-1.5">
          <span className="text-slate-500">{t("Selected:")}</span>
          <span className="text-blue-700 bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded">
            {selectedOrders.length}
          </span>
        </div>
        <span className="h-3 w-px bg-slate-300" />
        <div className="flex items-center gap-1.5">
          <span className="text-slate-500">{t("Total:")}</span>
          <span className="text-slate-700 bg-slate-50 border border-slate-200 px-1.5 py-0.5 rounded">
            {totalOrders}
          </span>
        </div>
      </div>,
    );

    return () => setHeaderButtons(null);
  }, [setHeaderButtons, selectedOrders, totalOrders, t]);

  // Pagination helpers
  const goToPage = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    setSelectedOrders([]);
  };

  const renderPageNumbers = () => {
    const pages: (number | "...")[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push("...");
      for (
        let i = Math.max(2, currentPage - 1);
        i <= Math.min(totalPages - 1, currentPage + 1);
        i++
      ) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div className="grid grid-cols-12 gap-6 p-2  bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl transition-all duration-300 w-ful rounded-r-3xl rounded-bl-3xl w-full">
      <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
      {/* Left Side: Table & Filters */}
      <div
        className={
          showRightPanel
            ? "col-span-12 lg:col-span-7 xl:col-span-8 space-y-6"
            : "col-span-12 space-y-6"
        }
      >
        {/* ── Main Card (Header + Table Joined) ─────────────────────── */}
        <div className="bg-white rounded-3xl overflow-visible">
          <div className="px-6 pb-1.5">
            {/* Header Row (Filters + Actions Unified) */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-3">
                {/* Merchant filter ── dynamic */}
              </div>

              {/* Right side: Export + Search + Bulk Actions + Selected Badge + Primary Button */}
              <div className="flex items-center flex-wrap gap-2.5">
                <NewSelectInput
                  name="merchant-filter"
                  placeholder={t("Merchant")}
                  value={selectedMerchant === "All" ? "" : selectedMerchant}
                  setValue={(val) => setSelectedMerchant(val || "All")}
                  selectOptions={uniqueMerchants.map((m: any) => ({
                    id: m,
                    value: m,
                    text: m,
                  }))}
                  className="w-fit"
                  removeDiv
                  compact
                />

                {/* Platform filter ── dynamic */}
                <NewSelectInput
                  name="platform-filter"
                  placeholder={t("Platform")}
                  value={selectedPlatform === "All" ? "" : selectedPlatform}
                  setValue={(val) => setSelectedPlatform(val || "All")}
                  selectOptions={uniquePlatforms.map((p: any) => ({
                    id: p,
                    value: p,
                    text: p,
                  }))}
                  className="w-fit"
                  removeDiv
                  compact
                />

                {/* Reset Button — only shown when a filter is active */}
                {hasActiveFilters && (
                  <button
                    onClick={handleReset}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 bg-rose-50 hover:bg-rose-100 border border-rose-200 hover:border-rose-300 text-rose-600 rounded-md transition shadow-sm active:scale-95 duration-150 text-[11px] font-bold"
                    title={t("Reset Filters")}
                  >
                    <IconRotateClockwise size={14} stroke={2.5} />
                  </button>
                )}

                {/* Sliding Search */}
                {/* Sliding Search */}
                <div className="relative flex items-center group">
                  <div
                    onClick={() => {
                      if (!showSearch) {
                        setShowSearch(true);
                      }
                    }}
                    className={`flex items-center transition-all duration-300 ease-in-out border border-slate-200 rounded-lg bg-white shadow-sm h-8 ${
                      showSearch
                        ? "w-64 px-2 cursor-default"
                        : "w-8 justify-center cursor-pointer hover:bg-slate-50 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95"
                    }`}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowSearch(!showSearch);
                      }}
                      className="flex items-center justify-center text-slate-700 focus:outline-none flex-shrink-0"
                    >
                      <IconSearch size={17} stroke={2.5} />
                    </button>
                    {showSearch && (
                      <>
                        <input
                          type="text"
                          value={searchFor}
                          onChange={(e) => setSearchFor(e.target.value)}
                          placeholder={t("Search...")}
                          autoFocus
                          className="flex-1 ml-2 bg-transparent text-xs text-slate-800 placeholder-slate-400 outline-none border-none"
                        />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSearchFor("");
                            setShowSearch(false);
                          }}
                          className="flex items-center justify-center p-0.5 ml-1 text-slate-400 hover:text-slate-600 focus:outline-none flex-shrink-0"
                          title={t("Clear & Close")}
                        >
                          <IconX size={14} stroke={2.5} />
                        </button>
                      </>
                    )}
                  </div>
                  {!showSearch && (
                    <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50">
                      {t("Search")}
                    </div>
                  )}
                </div>

                {/* Export */}
                <div className="relative group">
                  <button
                    onClick={handleExportXLSX}
                    className="w-8 h-8 flex items-center justify-center bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg transition-all duration-150 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer"
                  >
                    <IconDownload size={17} stroke={2.5} />
                  </button>
                  <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50">
                    {t("Export to Excel")}
                  </div>
                </div>

                {/* Bulk Status Change */}
                {canUpdateOrder && (
                  <div className="relative group">
                    <button
                      onClick={() => setShowBulkStatusModal(true)}
                      className="w-8 h-8 flex items-center justify-center bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg transition-all duration-150 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer"
                    >
                      <IconPackage size={17} stroke={2.5} />
                    </button>
                    <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50">
                      {t("Bulk Status Change")}
                    </div>
                  </div>
                )}

                {/* Entries Limit Selector */}
                {response?.pagination?.totalPages &&
                  response.pagination.totalPages * limit > 10 && (
                    <EntriesSelector
                      value={limit}
                      options={[
                        10,
                        ...(response.pagination.totalPages * limit > 10
                          ? [20]
                          : []),
                        ...(response.pagination.totalPages * limit > 20
                          ? [30]
                          : []),
                        ...(response.pagination.totalPages * limit > 50
                          ? [50]
                          : []),
                        ...(response.pagination.totalPages * limit > 50
                          ? [100]
                          : []),
                      ]}
                      onChange={(val) => {
                        setLimit(val);
                        setCurrentPage(1);
                      }}
                    />
                  )}

                {/* Columns Hide/Show Filter */}
                <div className="relative group">
                  <ColumnsFilter
                    columnsData={columns}
                    setHeadings={handleSetHeadings}
                  />
                  <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50">
                    {t("Columns View")}
                  </div>
                </div>

                {/* Picklist Button (only visible when selected > 0) */}
                {selectedOrders.length > 0 && canUpdateOrder && (
                  <button
                    onClick={handleGeneratePicklist}
                    disabled={isGeneratingPicklist}
                    className={`px-4 py-2 rounded-xl text-xs font-bold tracking-wide transition-all border select-none bg-[#FBD037] border-[#FBD037] text-[#131327] hover:bg-[#e2bd30] shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 duration-150 ${isGeneratingPicklist ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {isGeneratingPicklist ? t("Generating...") : `${t("Picklist")} (${selectedOrders.length})`}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Table Container (Joined) */}
          <div className="border-t border-slate-100">
            {isLoading ? (
              <div className="py-16 text-center bg-white">
                <p className="text-slate-400 text-sm font-semibold animate-pulse">
                  {t("Loading orders...")}
                </p>
              </div>
            ) : filteredOrders.length > 0 ? (
              <AllOrderTable
                data={{ orders: filteredOrders }}
                columnsData={columns}
                currentPage={currentPage}
                limitPage={limit}
                isCheckboxEnabled={true}
                selectedOrders={selectedOrders}
                onSelectOrder={handleSelectOrder}
                onViewDetails={(awb) => navigate(`/orders/${awb}`)}
              />
            ) : (
              <div className="py-16 text-center bg-white">
                <p className="text-slate-400 text-sm font-semibold">
                  {t("No orders found matching the filter")}
                </p>
              </div>
            )}
          </div>
        </div>

      </div>
      

      {showBulkStatusModal && (
        <BulkStatusUpdateModal
          onClose={() => setShowBulkStatusModal(false)}
          fetchData={refetch}
          targetStatus="ORDER_CREATED"
          />
        )}
        {/* ── Pagination ─────────────────────────────────────────── */}
        {totalPages > 1 && (
          <div className="flex items-center justify-end px-2 pb-4">
            {/* Right: page controls */}
            <div className="flex items-center gap-1">
              {/* Prev */}
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition shadow-sm"
              >
                <IconChevronLeft size={14} stroke={2.5} />
              </button>

              {/* Page numbers */}
              {renderPageNumbers().map((p, i) =>
                p === "..." ? (
                  <span
                    key={`ellipsis-${i}`}
                    className="w-8 h-8 flex items-center justify-center text-xs text-slate-400 font-bold"
                  >
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    onClick={() => goToPage(p as number)}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg border text-xs font-bold transition shadow-sm ${
                      currentPage === p
                        ? "bg-[#FBD037] border-[#FBD037] text-[#131327]"
                        : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    {p}
                  </button>
                ),
              )}

              {/* Next */}
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition shadow-sm"
              >
                <IconChevronRight size={14} stroke={2.5} />
              </button>
            </div>
          </div>
        )}
    </div>
  );
}
